/**
 * Headlines and detail text for AccionSistema cards (user resource audit).
 */

import es from "../data/locales/es.json";

/**
 * BSON ObjectId instances are objects without an `_id` property; normalize to hex string.
 * @param {object} ref
 * @returns {boolean}
 */
function isLikelyBsonObjectId(ref) {
  return typeof ref.toHexString === "function";
}

/**
 * @param {unknown} ref
 * @returns {string}
 */
function populatedId(ref) {
  if (ref == null) {
    return "";
  }
  const t = typeof ref;
  if (t === "string" || t === "number" || t === "boolean" || t === "bigint" || t === "symbol") {
    return String(ref);
  }
  if (t === "object" && ref !== null) {
    if ("_id" in ref) {
      const id = /** @type {{ _id?: unknown }} */ (ref)._id;
      if (id == null || id === "") {
        return "";
      }
      if (typeof id === "object" && id !== null && isLikelyBsonObjectId(id)) {
        try {
          return /** @type {{ toHexString: () => string }} */ (id).toHexString();
        } catch {
          return "";
        }
      }
      if (typeof id === "object" && id !== null) {
        return "";
      }
      return String(id);
    }
    if (isLikelyBsonObjectId(ref)) {
      try {
        return /** @type {{ toHexString: () => string }} */ (ref).toHexString();
      } catch {
        return "";
      }
    }
    return "";
  }
  return "";
}

/**
 * @param {unknown} ref
 * @returns {string}
 */
function populatedNombre(ref) {
  if (ref == null || typeof ref !== "object") {
    return "";
  }
  const n = /** @type {{ nombre?: unknown }} */ (ref).nombre;
  return typeof n === "string" ? n.trim() : "";
}

/**
 * @param {unknown} ref
 * @returns {string}
 */
function populatedRol(ref) {
  if (ref == null || typeof ref !== "object") {
    return "";
  }
  const r = /** @type {{ rol?: unknown }} */ (ref).rol;
  return typeof r === "string" ? r.trim() : "";
}

/**
 * @param {unknown} template
 * @param {unknown} vars
 * @returns {string}
 */
function tpl(template, vars) {
  const safeVars =
    vars != null && typeof vars === "object" && !Array.isArray(vars)
      ? /** @type {Record<string, string>} */ (vars)
      : {};
  const fallback =
    typeof es.activityLog?.templateFallback === "string"
      ? es.activityLog.templateFallback
      : "";
  const raw = typeof template === "string" ? template.trim() : "";
  const base = raw.length > 0 ? /** @type {string} */ (template) : fallback;
  if (typeof base !== "string" || base.length === 0) {
    return "";
  }
  return base.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(safeVars, key)
      ? String(safeVars[key] ?? "")
      : ""
  );
}

/**
 * @param {object|undefined|null} strings
 * @param {string} key
 * @returns {string}
 */
function pickActivityString(strings, key) {
  if (strings == null || typeof strings !== "object") {
    return "";
  }
  const v = /** @type {Record<string, unknown>} */ (strings)[key];
  return typeof v === "string" ? v : "";
}

/**
 * @param {string} obs
 * @returns {{ from: string, to: string } | null}
 */
export function parseRolCambioObservacion(obs) {
  if (typeof obs !== "string") {
    return null;
  }
  const m = obs.trim().match(/^\s*de\s*=\s*(\S+)\s+a\s*=\s*(\S+)\s*$/i);
  if (!m) {
    return null;
  }
  return { from: m[1], to: m[2] };
}

/**
 * @param {string} obs
 * @returns {{ flow: "alumno_email"|"staff", staffRol: string, emailSent: boolean|null } | null}
 */
export function parseRegistroUsuarioObservacion(obs) {
  if (typeof obs !== "string") {
    return null;
  }
  const norm = obs.trim();
  if (norm === "") {
    return null;
  }
  if (/registrar_y_enviar\s+flow\s*=\s*alumno_email\b/i.test(norm)) {
    return { flow: "alumno_email", staffRol: "", emailSent: null };
  }
  if (/registrar_y_enviar\s+staff\b/i.test(norm)) {
    const rolMatch = norm.match(/\brol\s*=\s*([A-Za-z0-9_-]+)/i);
    const emailMatch = norm.match(/\bemail_sent\s*=\s*(true|false)\b/i);
    return {
      flow: "staff",
      staffRol: rolMatch ? rolMatch[1] : "",
      emailSent: emailMatch ? emailMatch[1].toLowerCase() === "true" : null,
    };
  }
  return null;
}

/**
 * @param {unknown} strings
 * @returns {Record<string, string> | undefined}
 */
function getRoleLabels(strings) {
  if (strings == null || typeof strings !== "object") {
    return undefined;
  }
  const roles = /** @type {{ roles?: unknown }} */ (strings).roles;
  if (typeof roles === "object" && roles !== null) {
    return /** @type {Record<string, string>} */ (roles);
  }
  return undefined;
}

/**
 * @param {string} rolCode
 * @param {Record<string, string>|undefined} roleLabels
 * @returns {string}
 */
function labelRol(rolCode, roleLabels) {
  if (!rolCode || !roleLabels) {
    return rolCode;
  }
  const key = rolCode.trim();
  const mapped = roleLabels[key];
  return typeof mapped === "string" && mapped.length > 0 ? mapped : rolCode;
}

/**
 * @param {unknown} accion
 * @returns {boolean}
 */
function isAccionRecord(accion) {
  return accion != null && typeof accion === "object" && !Array.isArray(accion);
}

/**
 * Target profile for display / identity: snapshot when user row was missing, else idRecurso.
 * @param {object} accion
 * @returns {unknown}
 */
function accionTargetProfileRef(accion) {
  if (!isAccionRecord(accion)) {
    return null;
  }
  const snap = accion.idRecursoSnapshot;
  if (snap != null && typeof snap === "object" && !Array.isArray(snap)) {
    return snap;
  }
  return accion.idRecurso;
}

/**
 * @param {object} accion
 * @returns {string}
 */
function populatedIdForAccionTarget(accion) {
  const ref = accionTargetProfileRef(accion);
  if (ref != null && typeof ref === "object" && !Array.isArray(ref) && "usuarioId" in ref) {
    const uid = /** @type {{ usuarioId?: unknown }} */ (ref).usuarioId;
    if (uid != null && uid !== "") {
      return populatedId({ _id: uid });
    }
  }
  return populatedId(accion.idRecurso);
}

/**
 * @param {object} accion
 * @returns {string}
 */
function populatedNombreForAccionTarget(accion) {
  if (!isAccionRecord(accion)) {
    return "";
  }
  const ref = accionTargetProfileRef(accion);
  const fromRef = populatedNombre(ref);
  if (fromRef.trim() !== "") {
    return fromRef;
  }
  const ir = accion.idRecurso;
  if (typeof ir === "string" || typeof ir === "number") {
    return String(ir);
  }
  return populatedNombre(ir);
}

/**
 * @param {object} accion
 * @returns {string}
 */
function populatedRolForAccionTarget(accion) {
  if (!isAccionRecord(accion)) {
    return "";
  }
  const ref = accionTargetProfileRef(accion);
  const fromRef = populatedRol(ref);
  if (fromRef.trim() !== "") {
    return fromRef;
  }
  const ir = accion.idRecurso;
  if (ir != null && typeof ir === "object" && !Array.isArray(ir)) {
    return populatedRol(ir);
  }
  return "";
}

/**
 * @param {object} accion
 * @returns {boolean}
 */
export function isThirdPartyUserAccion(accion) {
  if (!isAccionRecord(accion)) {
    return false;
  }
  const actor = populatedId(accion.ejecutadoPor);
  const target = populatedIdForAccionTarget(accion);
  return actor.length > 0 && target.length > 0 && actor !== target;
}

/**
 * @param {object} accion
 * @param {(tipo: string) => string} getTipoLabel
 * @param {typeof es.activityLog} [strings]
 * @returns {string}
 */
export function buildAccionCardHeadline(accion, getTipoLabel, strings = es.activityLog) {
  if (!isAccionRecord(accion)) {
    return "";
  }
  const tipo = typeof accion.tipo === "string" ? accion.tipo.trim() : "";
  let actionLabel = "";
  if (typeof getTipoLabel === "function") {
    const out = getTipoLabel(tipo);
    if (out != null) {
      actionLabel = String(out);
    }
  }
  const unknown = pickActivityString(strings, "headlineUnknownUser");
  const actorName = populatedNombre(accion.ejecutadoPor) || unknown;
  const targetName =
    populatedNombreForAccionTarget(accion) || populatedNombre(accion.ejecutadoPor) || unknown;

  if (isThirdPartyUserAccion(accion)) {
    return tpl(pickActivityString(strings, "cardHeadlineThirdParty"), {
      actor: String(actorName),
      action: actionLabel,
    });
  }
  return tpl(pickActivityString(strings, "cardHeadlineSelf"), {
    subject: String(targetName),
    action: actionLabel,
  });
}

/**
 * @param {object} accion
 * @param {typeof es.activityLog} [strings]
 * @returns {string|null} null when the action is not third-party (same actor as target)
 */
export function buildAccionAffectedProfileLine(accion, strings = es.activityLog) {
  if (!isAccionRecord(accion)) {
    return null;
  }
  if (!isThirdPartyUserAccion(accion)) {
    return null;
  }
  const name = populatedNombreForAccionTarget(accion);
  const rolCode = populatedRolForAccionTarget(accion);
  const roleLabels = getRoleLabels(strings);
  let rolePart = "";
  if (rolCode && roleLabels) {
    const display = labelRol(rolCode, roleLabels);
    if (typeof display === "string" && display.trim() !== "") {
      rolePart = ` (${display.trim()})`;
    }
  }
  const unknown = pickActivityString(strings, "headlineUnknownUser");
  return tpl(pickActivityString(strings, "affectedProfileLine"), {
    name: String(name || unknown),
    rolePart: String(rolePart),
  });
}

/**
 * @param {object} accion
 * @param {typeof es.activityLog} [strings]
 * @returns {string}
 */
export function buildAccionDetailBody(accion, strings = es.activityLog) {
  if (!isAccionRecord(accion)) {
    return pickActivityString(strings, "noDetails");
  }
  const obs =
    typeof accion.observacion === "string" ? accion.observacion.trim() : "";
  const unknown = pickActivityString(strings, "headlineUnknownUser");
  const targetName = populatedNombreForAccionTarget(accion) || unknown;
  const actorName = populatedNombre(accion.ejecutadoPor) || unknown;
  const roleLabels = getRoleLabels(strings);

  const tipoRaw = typeof accion.tipo === "string" ? accion.tipo.trim() : "";
  const isThirdParty = isThirdPartyUserAccion(accion);

  if (tipoRaw === "rol_cambio") {
    const parsed = parseRolCambioObservacion(obs);
    if (parsed) {
      return tpl(pickActivityString(strings, "detailRolCambio"), {
        actor: String(actorName),
        target: String(targetName),
        from: labelRol(parsed.from, roleLabels),
        to: labelRol(parsed.to, roleLabels),
      });
    }
    if (obs) {
      return tpl(pickActivityString(strings, "detailRolCambioRaw"), {
        target: String(targetName),
        obs: String(obs),
      });
    }
  }

  if (tipoRaw === "permiso_cambio" && obs) {
    return tpl(pickActivityString(strings, "detailPermisoCambio"), {
      target: String(targetName),
      obs: String(obs),
    });
  }

  if (
    tipoRaw === "password_cambio" &&
    isThirdParty &&
    obs.includes("cambio_por_admin")
  ) {
    return tpl(pickActivityString(strings, "detailPasswordByAdmin"), {
      actor: String(actorName),
      target: String(targetName),
    });
  }

  if (
    (tipoRaw === "bloqueo" || tipoRaw === "desbloqueo") &&
    isThirdParty
  ) {
    const key =
      tipoRaw === "bloqueo" ? "detailBloqueoThirdParty" : "detailDesbloqueoThirdParty";
    return tpl(pickActivityString(strings, key), {
      actor: String(actorName),
      target: String(targetName),
    });
  }

  if (
    tipoRaw === "eliminacion" &&
    isThirdParty &&
    obs.includes("eliminacion_usuario")
  ) {
    return tpl(pickActivityString(strings, "detailEliminacionUsuarioHard"), {
      actor: String(actorName),
      target: String(targetName),
    });
  }

  const registroEmailFailed = /\bemail_sent\s*=\s*false\b/i.test(obs);
  if (tipoRaw === "registro_usuario" && registroEmailFailed) {
    const hint = pickActivityString(strings, "detailRegistroUsuarioEmailFailed");
    return obs && hint ? `${obs}\n\n${hint}` : obs || hint || pickActivityString(strings, "noDetails");
  }

  if (tipoRaw === "registro_usuario") {
    const parsed = parseRegistroUsuarioObservacion(obs);
    if (parsed && parsed.flow === "alumno_email") {
      const friendly = tpl(pickActivityString(strings, "detailRegistroUsuarioAlumnoEmail"), {
        actor: String(actorName),
        target: String(targetName),
      });
      if (friendly) {
        return friendly;
      }
    }
    if (parsed && parsed.flow === "staff" && parsed.emailSent === true) {
      const friendly = tpl(pickActivityString(strings, "detailRegistroUsuarioStaffEmail"), {
        actor: String(actorName),
        target: String(targetName),
        rol: labelRol(parsed.staffRol, roleLabels),
      });
      if (friendly) {
        return friendly;
      }
    }
  }

  if (obs) {
    return obs;
  }
  return pickActivityString(strings, "noDetails");
}
